import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Field = ({ label, required, error, hint, htmlFor, children }) => (
  <div className="space-y-2">
    {label && (
      <label htmlFor={htmlFor} className="block text-sm font-medium text-[#0B0A08]">
        {label}
        {required && <span className="ml-1 text-[#C6A04A]">*</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="text-xs text-[#0B0A08]/50">{hint}</p>}
    {error && (
      <p className="text-xs font-medium text-red-600" data-testid={`${htmlFor}-error`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  "w-full rounded-xl border-[#0B0A08]/15 bg-white text-[#0B0A08] focus-visible:ring-[#C6A04A]";

export const TextField = ({ id, error, ...props }) => (
  <Input
    id={id}
    data-testid={`field-${id}`}
    aria-invalid={!!error}
    className={`h-11 ${inputCls}`}
    {...props}
  />
);

export const TextAreaField = ({ id, error, ...props }) => (
  <Textarea
    id={id}
    data-testid={`field-${id}`}
    aria-invalid={!!error}
    className={`min-h-[110px] ${inputCls}`}
    {...props}
  />
);

export const SelectField = ({ id, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={`field-${id}`} className={`h-11 ${inputCls}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((opt) => (
        <SelectItem key={opt} value={opt} data-testid={`option-${id}-${opt.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
          {opt}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const PillGroup = ({ id, options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2" role="group" data-testid={`field-${id}`}>
    {options.map((opt) => {
      const active = selected.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          aria-pressed={active}
          data-testid={`pill-${id}-${opt.replace(/[^a-zA-Z0-9]+/g, "-")}`}
          onClick={() => onToggle(opt)}
          className={`rounded-full border px-4 py-2 text-sm transition-colors ${
            active
              ? "border-[#C6A04A] bg-[#C6A04A] text-[#0B0A08]"
              : "border-[#0B0A08]/20 bg-white text-[#0B0A08]/75 hover:border-[#C6A04A]"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export const RadioPills = ({ id, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={`field-${id}`}>
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          key={opt}
          type="button"
          role="radio"
          aria-checked={active}
          data-testid={`radio-${id}-${opt.replace(/[^a-zA-Z0-9]+/g, "-")}`}
          onClick={() => onChange(opt)}
          className={`rounded-full border px-4 py-2 text-sm transition-colors ${
            active
              ? "border-[#C6A04A] bg-[#C6A04A] text-[#0B0A08]"
              : "border-[#0B0A08]/20 bg-white text-[#0B0A08]/75 hover:border-[#C6A04A]"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export const CheckAck = ({ id, checked, onChange, error, children }) => (
  <div className="space-y-1">
    <label className="flex cursor-pointer items-start gap-3 text-sm text-[#0B0A08]/80">
      <input
        type="checkbox"
        id={id}
        data-testid={`field-${id}`}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-[#0B0A08]/30 accent-[#C6A04A]"
      />
      <span>{children}</span>
    </label>
    {error && (
      <p className="ml-8 text-xs font-medium text-red-600" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);
